import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import Login from './Login';
import Dashboard from './pages/Dashboard';
import Usuarios from './pages/Usuarios';
import Campanhas from './pages/Campanhas';
import NovaCampanha from './pages/NovaCampanha';
import DetalhesCampanha from './pages/DetalhesCampanha';
import EditarCampanha from './pages/EditarCampanha';
import Configuracoes from './pages/Configuracoes';
import Sips from './pages/Sips';
import PhoneNumber from './pages/PhoneNumber';
import CadastrarSIP from './pages/CadastrarSIP';
import CriarCampanha from './pages/CriarCampanha';
import Assistentes from './pages/Assistentes';
import Relatorios from './pages/Relatorios';
import CallLogs from './pages/CallLogs';
import Sidebar from './components/Sidebar';
import Tools from './pages/Tools';
import Files from './pages/Files';
import StructuredOutputs from './pages/StructuredOutputs';
import Cotacao from './pages/Cotacao';
import WavoipInstanciasWhatsApp from './pages/WavoipInstanciasWhatsApp';
import WhatsAppInstancias from './pages/WhatsAppInstancias';
import WhatsAppInstanciasAdmin from './pages/WhatsAppInstanciasAdmin';
import { getBrandConfig } from './brandConfig';
import { Toaster } from 'react-hot-toast';
import './App.css';
import ErrorBoundary from './components/ErrorBoundary';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  const tema = 'escuro';
  const [menuAberto, setMenuAberto] = useState(true);

  useEffect(() => {
    const root = document.documentElement;
    let brand = getBrandConfig();

    // Se existir branding específico de tenant no localStorage, mesclar por cima
    try {
      const tenantBrandingRaw = localStorage.getItem('tenantBranding');
      if (tenantBrandingRaw) {
        const tenantBranding = JSON.parse(tenantBrandingRaw);
        brand = {
          ...brand,
          logoUrl: tenantBranding.logoUrl || brand.logoUrl,
          theme: {
            ...brand.theme,
            ...(tenantBranding.theme || {}),
          },
        };
      }
    } catch (e) {
      console.error('Erro ao aplicar tenantBranding:', e);
    }

    document.body.classList.remove('claro');
    document.body.classList.add('escuro');
    root.classList.add('dark');

    if (brand.theme) {
      root.style.setProperty('--color-primary', brand.theme.primaryColor || '#4f46e5');
      root.style.setProperty('--color-secondary', brand.theme.secondaryColor || '#22c55e');
      root.style.setProperty('--color-bg', brand.theme.backgroundColor || '#020817');
      root.style.setProperty('--color-text', brand.theme.textColor || '#f9fafb');
    }
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      setIsAuthenticated(!!localStorage.getItem('token'));
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    const reqInterceptor = axios.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (!token || !String(token).trim()) return config;

      const t = String(token).trim();
      const headers = config.headers || {};
      const hasAuth = !!(headers.Authorization || headers.authorization);
      const hasXAccessToken = !!(headers['X-Access-Token'] || headers['x-access-token']);

      if (!hasAuth) headers.Authorization = `Bearer ${t}`;
      if (!hasXAccessToken) headers['X-Access-Token'] = t;
      config.headers = headers;
      return config;
    });

    const resInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const code = error?.response?.data?.code;
        const requestUrl = String(error?.config?.url || '');
        const isLoginRequest = /\/login(?:\?|$)/.test(requestUrl);
        const isAuthTokenError = code === 'TOKEN_EXPIRED' || code === 'TOKEN_INVALID';

        if (status === 401 && isAuthTokenError && !isLoginRequest) {
          localStorage.clear();
          if (window.location.pathname !== '/') {
            window.location.href = '/';
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
    };
  }, []);

  const Layout = ({ children }) => (
    <div className={`layout ${tema} flex min-h-screen w-full bg-background text-foreground`}>
      <Sidebar
        aberto={menuAberto}
        toggleMenu={() => setMenuAberto(!menuAberto)}
      />
      <div className="conteudo flex-1 overflow-y-auto bg-muted/30 p-6">
        {children}
      </div>
    </div>
  );

  return (
    <>
      <Toaster
        position="top-right"
        gutter={8}
        toastOptions={{
          duration: 4000,
          style: {
            background: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
            border: '1px solid hsl(var(--border))',
          },
        }}
      />
      <BrowserRouter>
      <Routes>
        <Route path="/" element={<Login onLoginSuccess={() => setIsAuthenticated(true)} />} />
        {isAuthenticated && (
          <>
            <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
            <Route path="/usuarios" element={<Layout><Usuarios /></Layout>} />
            <Route path="/campanhas" element={<Layout><Campanhas /></Layout>} />
            <Route path="/campanhas/nova" element={<Layout><NovaCampanha /></Layout>} />
            <Route path="/campanhas/:id" element={<Layout><DetalhesCampanha /></Layout>} />
            <Route path="/campanhas/:id/editar" element={<Layout><EditarCampanha /></Layout>} />
            <Route path="/configuracoes" element={<Layout><Configuracoes /></Layout>} />
            <Route path="/sips" element={<Layout><Sips /></Layout>} />
            <Route path="/phoneNumber" element={<Layout><PhoneNumber /></Layout>} />
            <Route path="/wavoip-instancias" element={<Layout><WavoipInstanciasWhatsApp /></Layout>} />
            <Route path="/sips/cadastrar" element={<Layout><CadastrarSIP /></Layout>} />
            <Route path="/nova-campanha" element={<Layout><CriarCampanha /></Layout>} />
            <Route path="/assistentes" element={<Layout><Assistentes /></Layout>} />
            <Route path="/tools" element={<Layout><Tools /></Layout>} />
            <Route path="/structured-outputs" element={<Layout><StructuredOutputs /></Layout>} />
            <Route
              path="/relatorios"
              element={
                <Layout>
                  <ErrorBoundary
                    fallback={
                      <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
                        Ocorreu um erro ao exibir Relatórios. Volte para outra tela e atualize a página (F5), ou tente novamente mais tarde.
                      </div>
                    }
                  >
                    <Relatorios />
                  </ErrorBoundary>
                </Layout>
              }
            />
            <Route
              path="/call-logs"
              element={
                <Layout>
                  <ErrorBoundary>
                    <CallLogs />
                  </ErrorBoundary>
                </Layout>
              }
            />
            <Route path="/files" element={<Layout><Files /></Layout>} />
            <Route path="/cotacao" element={<Layout><Cotacao /></Layout>} />
            <Route path="/whatsapp-instancias" element={<Layout><WhatsAppInstancias /></Layout>} />
            <Route path="/whatsapp-instancias/admin" element={<Layout><WhatsAppInstanciasAdmin /></Layout>} />
          </>
        )}
      </Routes>
    </BrowserRouter>
    </>
  );
}

export default App;
